<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="v-tracklify-scroll flex min-h-0 flex-1 flex-col overflow-y-auto">
    <div class="flex flex-col gap-1 px-2 pt-3 pb-2" v-if="workspace">
      <div
        class="flex h-13 cursor-pointer items-center gap-2.5 rounded-control px-3 transition-[background-color] duration-150 hover:bg-hover"
        @click.stop="wsOpen = !wsOpen"
      >
        <UserAvatar class="size-8.5! flex-none" :user="workspace" :isUser="false" noBorder />
        <div class="flex min-w-0 flex-1 flex-col gap-px">
          <span class="text-unit font-bold tracking-[0.06em] text-text-muted uppercase">{{ t('menu.workspace') }}</span>
          <span class="truncate text-body font-bold tracking-[-0.01em] text-text">{{ workspace.name }}</span>
        </div>
        <IconChevronDown
          class="size-3 flex-none text-[0.75rem] text-text-muted transition-transform duration-[180ms]"
          :class="{ 'rotate-180': wsOpen }"
        />
      </div>

      <template v-if="wsOpen">
        <router-link :to="{ name: 'workspaceSettings' }" :class="workspaceRowClass(false)">
          <IconCog6Tooth :class="ROW_ICON_CLASS" />
          {{ t('menu.workspace_settings') }}
        </router-link>

        <div :class="DIVIDER_CLASS"></div>

        <div
          v-for="ws in workspaces"
          :key="`ws-${ws.id}`"
          :class="workspaceRowClass(ws.id === workspace.id)"
          @click="switchWorkspace(ws)"
        >
          <UserAvatar class="size-6.5! flex-none" :user="ws" :isUser="false" noBorder />
          <span class="min-w-0 flex-1 truncate">{{ ws.name }}</span>
          <IconCheck16Solid v-if="ws.id === workspace.id" :class="ROW_ICON_CLASS" />
        </div>

        <div :class="[workspaceRowClass(false), 'font-bold! text-accent!']" @click="createWorkspace">
          <span class="flex size-6.5 flex-none items-center justify-center rounded-[0.4375rem] border border-dashed border-accent/50">
            <IconPlus :class="ROW_ICON_CLASS" />
          </span>
          {{ t('menu.create_new_workspace') }}
        </div>
      </template>
    </div>

    <div :class="WIDE_DIVIDER_CLASS" v-if="workspace"></div>

    <div :class="[LIST_CLASS, 'pt-1']">
      <router-link
        v-for="item in reportLinks"
        :key="item.routeName"
        :to="item.to"
        :class="navItemClass(route.name === item.routeName)"
      >
        <div :class="NAV_INNER_CLASS">
          <component :is="item.icon" :class="NAV_ICON_CLASS" />
          {{ t(item.label) }}
        </div>
      </router-link>
    </div>
    <div :class="WIDE_DIVIDER_CLASS" v-if="sortedProjectsList.length"></div>
    <div
      class="px-6 py-2 text-label font-bold tracking-[0.06em] text-text-muted uppercase"
      v-if="sortedProjectsList.length"
    >
      {{ t('menu.projects') }}
    </div>
    <div :class="LIST_CLASS">
      <router-link
        v-for="(project, index) in sortedProjectsList"
        :key="`project_${project.id}`"
        :class="navItemClass(isCurrentProject(project))"
        :to="projectRoute(project)"
        :ref="`project_${index}`"
        :title="project.name">
        <div :class="NAV_INNER_CLASS">
          <ProjectAvatar :class="PROJECT_AVATAR_CLASS" :project="project" />
          <div :class="PROJECT_NAME_CLASS">{{ project.name }}</div>
          <span
            v-if="isCurrentProject(project)"
            class="pointer-events-auto ml-auto flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-chip text-inherit transition-[background-color] duration-150 hover:bg-black/14"
            :title="t('menu.settings')"
            @click.prevent="openProjectSettings(project)"
          >
            <IconCog6Tooth class="text-base" />
          </span>
        </div>
      </router-link>
    </div>
    <div class="shrink-0 cursor-pointer overflow-hidden px-2" v-if="projectsArchivedList.length">
      <div
        class="archive__title flex h-10 items-center gap-2 rounded-control px-3 text-[0.8125rem] font-bold text-text-muted transition-[background-color,color] duration-150 hover:bg-hover hover:text-text"
        @click="collapseArchive = !collapseArchive"
      >
        {{t('settings.archive', 2)}}
        <IconChevronLeft16Solid
          class="shrink-0 text-body transition-transform duration-300"
          :class="{ '-rotate-90': !collapseArchive }"
        />
      </div>
      <div class="flex flex-col gap-1 overflow-x-hidden" :class="{ 'h-0': collapseArchive }">
        <router-link
          v-for="(project, index) in projectsArchivedList"
          :key="`project_${project.id}`"
          :class="navItemClass(isCurrentProject(project))"
          :to="projectRoute(project)"
          :ref="`project_${index}`"
          :title="project.name">
          <ProjectAvatar :class="PROJECT_AVATAR_CLASS" :project="project" />
          <div :class="NAV_INNER_CLASS">
            <div :class="PROJECT_NAME_CLASS">{{ project.name }}</div>
          </div>
        </router-link>
      </div>
    </div>
    <div class="px-2" v-if='!props.onlySelect'>
      <router-link
        :to="{ name: 'createProject' }"
        :class="navItemClass(route.name === 'createProject', 'text-accent')"
      >
        <div :class="NAV_INNER_CLASS">
          <IconPlus class="size-[0.9375rem] text-[0.9375rem]" />
          {{ t('menu.create_project') }}
        </div>
      </router-link>
    </div>
    </div>

    <div
      class="flex-none border-t border-line p-2"
      v-if='!props.onlySelect && project && project.slug'
    >
      <router-link
        :to="{
          name: 'projectSettings',
          params: {
            projectSlug: project.slug,
            slugNameProject: project.nameSlug || project.slug,
          }
        }"
        :class="[navItemClass(route.name === 'projectSettings'), 'h-10.5!']"
      >
        <div :class="NAV_INNER_CLASS">
          <IconCog6Tooth :class="NAV_ICON_CLASS" />
          <div class="max-w-[8.75rem] overflow-x-hidden text-ellipsis whitespace-pre">{{ project.name }}&nbsp;{{ t(`menu.settings`) }}</div>
        </div>
      </router-link>
    </div>
    <CreateWorkspaceDialog v-if="createWorkspaceOpen" @close="createWorkspaceOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import UserAvatar from '@/components/elements/UserAvatar';
import ProjectAvatar from '@/components/elements/ProjectAvatar';
import CreateWorkspaceDialog from '@/components/widgets/CreateWorkspaceDialog.vue';
import { callAPI } from '@/store/socket';
import { IconChartBar16Solid, IconCheck16Solid, IconChevronDown, IconChevronLeft16Solid, IconCog6Tooth, IconCreditCard, IconPlus, IconDocumentText, IconCalendarDateRange20Solid } from '@iconify-prerendered/vue-heroicons';

// Props
const props = defineProps({
  onlySelect: {
    type: Boolean,
    default: false,
  },
});

// Composables
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Reactive data
const selectedProjectIndex = ref(-1);
const collapseArchive = ref(true);
const wsOpen = ref(false);

// Computed properties
const workspace = computed(() => store.state.user.workspace);
const workspaces = computed(() => store.state.user.workspaces);
const project = computed(() => store.state.project.project);
const projectsList = computed(() => store.state.project.projectsList);
const projectsArchivedList = computed(() => store.state.project.projectsArchivedList);
const profitShareVisible = ref(false);
const workspaceId = computed(() => store.state.user.workspace?.id);
const workspaceRole = computed(() => store.state.user.workspace?.users
  ?.find(member => member.id === store.state.user.user?.id)?.userWorkspace?.role);
const showProfitShare = computed(() => workspaceRole.value === 'ADMIN' || profitShareVisible.value);

const reportLinks = computed(() => [
  { routeName: 'workspace-summary', to: { name: 'workspace-summary' }, icon: IconDocumentText, label: 'menu.summary_report' },
  { routeName: 'tasks-time-spent', to: { name: 'tasks-time-spent' }, icon: IconChartBar16Solid, label: 'menu.time_reports' },
  { routeName: 'salary', to: { name: 'salary', params: { salary_project_id: 'last' } }, icon: IconCreditCard, label: 'menu.salary' },
  { routeName: 'dayoff', to: { name: 'dayoff' }, icon: IconCalendarDateRange20Solid, label: 'menu.dayoff' },
  ...(showProfitShare.value
    ? [{ routeName: 'profit-share', to: { name: 'profit-share', params: { profit_share_project_id: 'last' } }, icon: IconCreditCard, label: 'menu.profit_share' }]
    : []),
]);

const LIST_CLASS = 'flex w-full flex-col justify-start gap-1 px-2 pb-1';
const DIVIDER_CLASS = 'mx-3 mt-1.25 mb-1.5 h-px bg-line';
const WIDE_DIVIDER_CLASS = 'mx-4 my-1 h-px bg-line';
const NAV_INNER_CLASS = 'flex size-full items-center gap-3';
const NAV_ICON_CLASS = 'shrink-0 text-[1.0625rem]';
const ROW_ICON_CLASS = 'flex-none text-[0.9375rem]';
const PROJECT_AVATAR_CLASS = 'size-7 shrink-0 rounded-chip';
const PROJECT_NAME_CLASS = 'max-w-[9.375rem] overflow-x-hidden text-ellipsis whitespace-pre';

// selected rows drop the hover classes: Tailwind's hover variant would otherwise win over them
const ROW_BASE = 'flex h-10 cursor-pointer items-center rounded-control text-body font-medium transition-[background-color,color] duration-150';
const navItemClass = (selected, color = 'text-text') => [
  ROW_BASE,
  'gap-3 px-3',
  selected ? 'pointer-events-none cursor-auto bg-accent text-on-accent' : `${color} hover:bg-accent-soft hover:text-accent`,
];
const workspaceRowClass = (current) => [
  ROW_BASE,
  'gap-2.75 px-2.5',
  current ? 'bg-accent text-on-accent' : 'text-text hover:bg-accent-soft hover:text-accent',
];

const isCurrentProject = (proj) => proj.slug === route.params.projectSlug && route.name === 'project';
const projectRoute = (proj) => ({
  name: 'project',
  params: { projectSlug: proj.slug, slugNameProject: proj.nameSlug },
});

async function loadProfitShareAccess() {
  try {
    const access = await callAPI({ method: 'getProfitShareAccess', params: {} });
    profitShareVisible.value = !!access?.visible;
  } catch (error) {
    console.error('Failed to load Profit-share access', error);
  }
}

const sortedProjectsList = computed(() => {
  if (!projectsList.value || !projectsList.value.length) {
    return [];
  }
  const routeProjectSlug = route.params ? route.params.projectSlug : null;

  let list = [...projectsList.value];
  list.sort((a, b) => {
    const first = a.projectMembers[0].lastActivity; // projectMembers[0] is you
    const second = b.projectMembers[0].lastActivity;
    if (routeProjectSlug) {
      if (routeProjectSlug === a.slug) return -1;
      if (routeProjectSlug === b.slug) return 1;
    }
    return dayjs(second) - dayjs(first);
  });
  list = list.filter(p => p.id !== 'last' && p.id !== 'No-project' && p.id !== 'No-projects' && p.archived === false);

  if (routeProjectSlug) {
    if (route.name === 'project' && route.params.projectSlug) {
      // make active project to first place
      list.push(list.shift());
    }
  }
  return list;
});

// Watchers
watch(sortedProjectsList, () => {
  selectedProjectIndex.value = sortedProjectsList.value.length - 1;
});

watch(workspaceId, loadProfitShareAccess);
onMounted(loadProfitShareAccess);
onMounted(() => store.dispatch('project/actionGetProjectsListArchived'));

const openProjectSettings = (proj) => {
  router.push({
    name: 'projectSettings',
    params: {
      projectSlug: proj.slug,
      slugNameProject: proj.nameSlug || proj.slug,
    },
  });
};

const switchWorkspace = async (ws) => {
  if (ws.id === workspace.value.id) {
    wsOpen.value = false;
    return;
  }
  await store.dispatch('user/actionSwitchWorkspace', ws.id);
};

const createWorkspaceOpen = ref(false);

const createWorkspace = () => {
  wsOpen.value = false;
  createWorkspaceOpen.value = true;
};
</script>
